import { getCurrentAdminRbacPermissionsOptions } from '@gozoki/api'
import { workShiftClockActionEnum } from '@gozoki/api-types'
import { useQuery } from '@tanstack/react-query'
import { api } from '~/lib/tuyau'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import z from 'zod'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { WorkshiftAttendanceAccessDenied } from '~/components/routes/workshift-attendance/workshift-attendance-access-denied'
import { WorkshiftAttendanceHistoryDialog } from '~/components/routes/workshift-attendance/workshift-attendance-history-dialog'
import { WorkShiftAttendanceLog } from '~/components/routes/workshift-attendance/workshift-attendance-log'
import { Button } from '~/components/ui/button'
import { useMissingPermissions, useRbacV2Enabled } from '~/utils/rbac'
import { parseRbacContract } from '~/utils/rbac-session'
import {
  canClockInStore,
  CLOCKING_ADD_PERMISSION,
  isTabletOnlyAccount,
} from '~/utils/tablet-account'

export const Route = createFileRoute('/_nosidebar/workshift-attendance/$storeId')({
  component: RouteComponent,
  // `_nosidebar` n'a pas de layout qui injecte l'ability dans le contexte du routeur (contrairement
  // à `/_authenticated`) : `withRoutePermissions` n'a rien à interroger dans un `beforeLoad`. La
  // garde est donc posée dans le composant, avec la même exigence, déclarée ici pour que les liens
  // et les outils qui lisent `staticData.permission` voient la même règle.
  staticData: { permission: CLOCKING_ADD_PERMISSION },
  validateSearch: z.object({
    collaboratorId: z.number().optional(),
    action: z.enum(workShiftClockActionEnum).optional(),
  }),
  params: {
    parse: z.object({ storeId: z.coerce.number() }).parse,
  },
})

function RouteComponent() {
  const { storeId } = Route.useParams()
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)

  // Garde d'écran : créer un pointage. En régime hérité l'ability autorise tout tuple v2 valide,
  // la garde ne discrimine donc qu'en v2 — exactement le régime où les comptes de pointage n'ont
  // plus de bit.
  const missingPermissions = useMissingPermissions(CLOCKING_ADD_PERMISSION)

  // Le périmètre boutique des grants ne se lit pas dans l'ability (CASL ne porte que le couple
  // action/ressource) : il vient du contrat lui-même. Contrat illisible ⇒ on ne bloque pas, le
  // serveur reste l'autorité (`assertAdminClockingTarget`).
  const { data: rbacPermissions } = useQuery({
    ...getCurrentAdminRbacPermissionsOptions(),
    enabled: missingPermissions.length === 0,
  })
  const rbacContract = parseRbacContract(rbacPermissions)
  const isStoreInScope = canClockInStore(rbacContract, storeId)

  // Un compte mixte (poste ADV distanciel qui cumule le rôle tablette, #905) arrive ici par le
  // menu et doit pouvoir revenir au back-office. Une tablette pure est un poste en mode borne : son
  // écran est le pointage, on ne l'invite pas à en sortir. En régime hérité, la tablette n'a pas de
  // menu : pas de lien non plus.
  const canLeaveToBackOffice = useRbacV2Enabled() && !isTabletOnlyAccount(rbacContract)

  const { data: store, isLoading: isLoadingStore } = useQuery({
    ...api.stores.show.queryOptions({ params: { id: storeId } }),
    enabled: missingPermissions.length === 0 && isStoreInScope,
  })
  const now = DateTime.now()

  if (missingPermissions.length > 0) {
    return <RbacAccessDeniedPage missingPermissions={missingPermissions} />
  }

  if (!isStoreInScope) {
    return <WorkshiftAttendanceAccessDenied />
  }

  if (isLoadingStore && !store) {
    return <div>Chargement...</div>
  }

  if (!store) {
    return <div>Boutique non trouvée</div>
  }

  return (
    <div className="p-2 h-dvh flex flex-col">
      <AppHeader>
        <Title
          subtitle={`${now.toLocaleString(DateTime.DATE_HUGE)} • ${now.toLocaleString(DateTime.TIME_24_SIMPLE)}`}
        >
          {store.label}
        </Title>

        <div className="ml-auto flex items-center gap-2">
          {canLeaveToBackOffice && (
            <Button variant="ghost" className="text-lg" size="lg" render={<Link to="/" />}>
              <ArrowLeft className="size-5" />
              Back-office
            </Button>
          )}

          <Button
            variant="outline"
            className="text-lg"
            size="lg"
            onClick={() => setIsHistoryOpen(true)}
          >
            Historique
          </Button>
        </div>
      </AppHeader>

      <AppContainer>
        <WorkShiftAttendanceLog storeName={store.label} storeId={storeId} />
      </AppContainer>

      <WorkshiftAttendanceHistoryDialog
        open={isHistoryOpen}
        onOpenChange={setIsHistoryOpen}
        storeId={storeId}
        storeName={store.label}
      />
    </div>
  )
}
